<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Compact Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-3'
      ]"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <!-- Back Button -->
          <button
            :class="[
              theme.isDark.value
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
              'p-2 rounded-lg transition-all'
            ]"
            title="Back to Compliance"
            @click="goBack"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <!-- Trophy Icon -->
          <div
            :class="[
              theme.isDark.value ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50',
              'flex items-center justify-center w-9 h-9 rounded-lg'
            ]"
          >
            <svg
              :class="[theme.isDark.value ? 'text-purple-400' : 'text-purple-600', 'w-5 h-5']"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h1
              :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-xl font-bold']"
            >
              Compliance Breakdown
            </h1>
            <div class="flex items-center space-x-2 mt-0.5">
              <span
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium'
                ]"
              >
                {{ teamDisplayName }}
              </span>
              <span
                v-if="subTeamDisplayName"
                :class="[theme.isDark.value ? 'text-gray-500' : 'text-gray-400', 'text-xs']"
              >
                /
              </span>
              <span
                v-if="subTeamDisplayName"
                :class="[
                  theme.isDark.value ? 'text-blue-400' : 'text-blue-600',
                  'text-xs font-semibold'
                ]"
              >
                {{ subTeamDisplayName }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <!-- View PRs Button -->
          <button
            :class="[
              theme.isDark.value
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white',
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center shadow-sm hover:shadow'
            ]"
            @click="navigateToPRList"
          >
            <svg
              class="w-3.5 h-3.5 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            View PRs
          </button>
          <!-- Refresh Button -->
          <button
            :disabled="loading"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-medium transition-all flex items-center shadow-sm hover:shadow',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="fetchDetails"
          >
            <svg
              :class="[loading ? 'animate-spin' : '', 'w-3.5 h-3.5 mr-1.5']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Compact Filters -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-3 shadow-sm'
        ]"
      >
        <div class="flex flex-wrap items-center gap-4">
          <!-- Time Range -->
          <div class="flex items-center space-x-2">
            <label
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium whitespace-nowrap'
              ]"
            >
              Time Range:
            </label>
            <select
              v-model="filters.timeRange"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="fetchDetails"
            >
              <option value="7d">Last 7 Days</option>
              <option value="14d">Last 14 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>

          <!-- Divider -->
          <div :class="[theme.isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6']"></div>

          <!-- Team -->
          <div class="flex items-center space-x-2">
            <span
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium'
              ]"
            >
              Team:
            </span>
            <select
              v-model="filters.team"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="onTeamChange"
            >
              <option
                v-for="team in teams"
                :key="team"
                :value="team"
              >
                {{ leaderboardService.formatTeamName(team) }}
              </option>
            </select>
          </div>

          <!-- Sub-Team -->
          <div class="flex items-center space-x-2">
            <span
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium'
              ]"
            >
              Sub-Team:
            </span>
            <select
              v-model="filters.subTeam"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="fetchDetails"
            >
              <option value="">All Sub-Teams</option>
              <option
                v-for="subTeam in subTeams"
                :key="subTeam"
                :value="subTeam"
              >
                {{ leaderboardService.formatSubTeamName(subTeam) }}
              </option>
            </select>
          </div>

          <!-- Quality Gate -->
          <div class="flex items-center space-x-2">
            <span
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium'
              ]"
            >
              Quality Gate:
            </span>
            <select
              v-model="filters.qualityGate"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="fetchDetails"
            >
              <option value="">All Gates</option>
              <option
                v-for="gate in qualityGates"
                :key="gate"
                :value="gate"
              >
                {{ leaderboardService.formatGateName(gate) }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div
        v-if="detailsData"
        class="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <!-- Total PRs Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Total PRs
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-purple-400' : 'text-purple-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ detailsData.totalPRs }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-purple-400' : 'text-purple-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Quality Gates Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Quality Gates
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-blue-400' : 'text-blue-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ detailsData.gateStats.length }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-blue-400' : 'text-blue-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Failing Gates Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Gates with Failures
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-red-400' : 'text-red-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ totalFailingGates }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-red-900 bg-opacity-30' : 'bg-red-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-red-400' : 'text-red-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-xl p-6 shadow-sm'
        ]"
      >
        <div class="flex items-start space-x-4">
          <div class="flex-shrink-0">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full bg-red-200 dark:bg-red-800"
            >
              <svg
                class="h-5 w-5 text-red-600 dark:text-red-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <div class="flex-1">
            <h3 class="text-base font-semibold text-red-800 dark:text-red-200">
              Error Loading Details
            </h3>
            <p class="mt-1.5 text-sm text-red-700 dark:text-red-300">{{ error }}</p>
            <button
              :class="[
                theme.isDark.value
                  ? 'bg-red-800 hover:bg-red-700 text-red-100'
                  : 'bg-red-600 hover:bg-red-700 text-white',
                'mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow'
              ]"
              @click="fetchDetails"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading && !detailsData"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-xl p-12 shadow-sm'
        ]"
      >
        <div class="flex flex-col items-center justify-center space-y-4">
          <svg
            class="animate-spin h-12 w-12 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <div class="text-center">
            <p
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'text-base font-medium'
              ]"
            >
              Loading gate breakdown...
            </p>
            <p :class="[theme.isDark.value ? 'text-gray-500' : 'text-gray-500', 'text-xs mt-1']">
              Analyzing quality gate results
            </p>
          </div>
        </div>
      </div>

      <!-- Gate Stats Table -->
      <div
        v-else-if="detailsData && sortedGateStats.length > 0"
        class="relative"
      >
        <!-- Loading Overlay -->
        <div
          v-if="loading"
          class="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-20 rounded-xl backdrop-blur-sm"
        >
          <div class="flex flex-col items-center">
            <svg
              class="animate-spin h-10 w-10 text-blue-500 mb-3"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <p class="text-white text-sm font-medium">Updating data...</p>
          </div>
        </div>

        <!-- Table Container -->
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-xl overflow-hidden shadow-sm'
          ]"
        >
          <!-- Table Header -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200',
              'border-b'
            ]"
          >
            <div class="grid grid-cols-12 gap-2 px-4 py-2.5">
              <div
                class="col-span-3 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('name')"
              >
                <div class="flex items-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Quality Gate
                  </span>
                  <svg
                    v-if="sortState.column === 'name'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div
                class="col-span-1 text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('pass')"
              >
                <div class="flex items-center justify-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Pass
                  </span>
                  <svg
                    v-if="sortState.column === 'pass'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div
                class="col-span-1 text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('fail')"
              >
                <div class="flex items-center justify-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Fail
                  </span>
                  <svg
                    v-if="sortState.column === 'fail'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div
                class="col-span-1 text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('warning')"
              >
                <div class="flex items-center justify-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Warning
                  </span>
                  <svg
                    v-if="sortState.column === 'warning'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div
                class="col-span-1 text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('total')"
              >
                <div class="flex items-center justify-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Total
                  </span>
                  <svg
                    v-if="sortState.column === 'total'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div
                class="col-span-4 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded px-2 py-1 -mx-2 -my-1 select-none"
                @click="sortBy('passRate')"
              >
                <div class="flex items-center space-x-1">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                      'text-xs font-semibold uppercase tracking-wider'
                    ]"
                  >
                    Performance
                  </span>
                  <svg
                    v-if="sortState.column === 'passRate'"
                    class="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      v-if="sortState.order === 'asc'"
                      fill-rule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clip-rule="evenodd"
                    />
                    <path
                      v-else
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div class="col-span-1 text-center">
                <span
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                    'text-xs font-semibold uppercase tracking-wider'
                  ]"
                >
                  Actions
                </span>
              </div>
            </div>
          </div>

          <!-- Table Body -->
          <div>
            <div
              v-for="(stat, index) in sortedGateStats"
              :key="stat.gateName"
              :class="[
                theme.isDark.value
                  ? 'hover:bg-gray-700 border-gray-700'
                  : 'hover:bg-gray-50 border-gray-200',
                'grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 transition-colors items-center'
              ]"
            >
              <!-- Quality Gate Name -->
              <div class="col-span-3 flex items-center space-x-2">
                <div
                  :class="[
                    stat.passRate >= 100
                      ? theme.isDark.value
                        ? 'bg-green-900 bg-opacity-30 text-green-400'
                        : 'bg-green-50 text-green-600'
                      : stat.passRate >= 80
                        ? theme.isDark.value
                          ? 'bg-yellow-900 bg-opacity-30 text-yellow-400'
                          : 'bg-yellow-50 text-yellow-600'
                        : theme.isDark.value
                          ? 'bg-red-900 bg-opacity-30 text-red-400'
                          : 'bg-red-50 text-red-600',
                    'flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold'
                  ]"
                >
                  {{ index + 1 }}
                </div>
                <span
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'font-medium text-sm'
                  ]"
                >
                  {{ leaderboardService.formatGateName(stat.gateName) }}
                </span>
              </div>

              <!-- Pass -->
              <div class="col-span-1 text-center">
                <span
                  :class="[
                    theme.isDark.value
                      ? 'bg-green-900 bg-opacity-30 text-green-400'
                      : 'bg-green-50 text-green-700',
                    'inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold'
                  ]"
                >
                  {{ stat.pass }}
                </span>
              </div>

              <!-- Fail -->
              <div class="col-span-1 text-center">
                <span
                  :class="[
                    theme.isDark.value
                      ? 'bg-red-900 bg-opacity-30 text-red-400'
                      : 'bg-red-50 text-red-700',
                    'inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold'
                  ]"
                >
                  {{ stat.fail }}
                </span>
              </div>

              <!-- Warning -->
              <div class="col-span-1 text-center">
                <span
                  :class="[
                    theme.isDark.value
                      ? 'bg-yellow-900 bg-opacity-30 text-yellow-400'
                      : 'bg-yellow-50 text-yellow-700',
                    'inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold'
                  ]"
                >
                  {{ stat.warning }}
                </span>
              </div>

              <!-- Total -->
              <div class="col-span-1 text-center">
                <span
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'font-semibold text-sm'
                  ]"
                >
                  {{ stat.total }}
                </span>
              </div>

              <!-- Performance -->
              <div class="col-span-4">
                <div class="space-y-1">
                  <!-- Pass Rate & Feedback -->
                  <div class="flex items-center justify-between">
                    <span
                      :class="[
                        stat.passRate >= 100
                          ? theme.isDark.value
                            ? 'text-green-400'
                            : 'text-green-600'
                          : stat.passRate >= 80
                            ? theme.isDark.value
                              ? 'text-yellow-400'
                              : 'text-yellow-600'
                            : theme.isDark.value
                              ? 'text-red-400'
                              : 'text-red-600',
                        'text-sm font-bold'
                      ]"
                    >
                      {{ stat.passRate }}%
                    </span>
                    <span
                      :class="[
                        theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                        'text-xs font-medium'
                      ]"
                    >
                      {{
                        stat.passRate >= 100
                          ? 'Compliant'
                          : stat.passRate >= 80
                            ? 'At Risk'
                            : 'Non-Compliant'
                      }}
                    </span>
                  </div>
                  <!-- Single Color Progress Bar -->
                  <div
                    class="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden"
                  >
                    <div
                      :class="[
                        stat.passRate >= 100
                          ? 'bg-green-500'
                          : stat.passRate >= 80
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      ]"
                      :style="{ width: `${stat.passRate}%` }"
                      class="h-full transition-all duration-500"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="col-span-1 text-center">
                <button
                  :class="[
                    theme.isDark.value
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white',
                    'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow-sm hover:shadow'
                  ]"
                  @click.stop="navigateToPRListForGate(stat.gateName)"
                >
                  View PRs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="detailsData && sortedGateStats.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-xl p-16 text-center shadow-sm'
        ]"
      >
        <div class="flex flex-col items-center justify-center space-y-4">
          <!-- Empty State Icon -->
          <div
            class="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700"
          >
            <svg
              class="w-8 h-8 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <!-- Empty State Text -->
          <div class="max-w-md">
            <p
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'text-base font-semibold mb-1'
              ]"
            >
              No Quality Gate Data Available
            </p>
            <p :class="[theme.isDark.value ? 'text-gray-500' : 'text-gray-500', 'text-sm']">
              No quality gate results found for this team in the selected time range. Try adjusting
              your filters or check back later.
            </p>
          </div>
          <!-- Optional Action Button -->
          <button
            :class="[
              theme.isDark.value
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white',
              'px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow mt-2'
            ]"
            @click="fetchDetails"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <!-- Generated At -->
      <div
        v-if="detailsData"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200',
          'border rounded-lg p-3 text-center'
        ]"
      >
        <div class="flex items-center justify-center space-x-2">
          <svg
            class="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span
            :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium']"
          >
            Data generated at {{ formatGeneratedAt(detailsData.generatedAt) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, inject, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import { leaderboardService } from '../../services/quality-gates'
import type { LeaderboardDetailsResponse, TimeRange } from '../../types/quality-gates'
import { logger } from '../../utils/logger'

const router = useRouter()
const route = useRoute()
const theme = inject('theme', useTheme())

// State
const detailsData = ref<LeaderboardDetailsResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const teams = ref<string[]>([])
const subTeams = ref<string[]>([])
const qualityGates = ref<string[]>([])

// Filters from query params
const filters = reactive({
  team: '',
  subTeam: '',
  qualityGate: '',
  timeRange: '7d' as TimeRange
})

// Sort state
const sortState = reactive({
  column: 'passRate' as 'name' | 'pass' | 'fail' | 'warning' | 'total' | 'passRate',
  order: 'asc' as 'asc' | 'desc'
})

// Computed
const teamDisplayName = computed(() => {
  return leaderboardService.formatTeamName(filters.team)
})

const subTeamDisplayName = computed(() => {
  return filters.subTeam ? leaderboardService.formatSubTeamName(filters.subTeam) : ''
})

const qualityGateDisplayName = computed(() => {
  return filters.qualityGate ? leaderboardService.formatGateName(filters.qualityGate) : ''
})

const updateURL = () => {
  const query: Record<string, string> = {
    team: filters.team,
    timeRange: filters.timeRange
  }

  if (filters.subTeam) {
    query.subTeam = filters.subTeam
  }

  if (filters.qualityGate) {
    query.qualityGate = filters.qualityGate
  }

  router.replace({ query })
}

const sortedGateStats = computed(() => {
  if (!detailsData.value) return []

  return [...detailsData.value.gateStats].sort((a, b) => {
    let comparison = 0

    switch (sortState.column) {
      case 'name':
        comparison = a.gateName.localeCompare(b.gateName)
        break
      case 'pass':
        comparison = a.pass - b.pass
        break
      case 'fail':
        comparison = a.fail - b.fail
        break
      case 'warning':
        comparison = a.warning - b.warning
        break
      case 'total':
        comparison = a.total - b.total
        break
      case 'passRate':
        comparison = a.passRate - b.passRate
        break
    }

    return sortState.order === 'asc' ? comparison : -comparison
  })
})

const totalFailingGates = computed(() => {
  if (!detailsData.value) return 0
  return detailsData.value.gateStats.filter((stat) => stat.fail > 0).length
})

// Methods
const fetchDetails = async () => {
  if (!filters.team) {
    error.value = 'Team parameter is required'
    return
  }

  try {
    loading.value = true
    error.value = null

    updateURL()

    logger.log('Fetching leaderboard details:', filters)

    const data = await leaderboardService.fetchLeaderboardDetails({
      team: filters.team,
      subTeam: filters.subTeam || undefined,
      qualityGate: filters.qualityGate || undefined,
      timeRange: filters.timeRange
    })

    detailsData.value = data

    logger.log('Details loaded:', {
      team: data.team,
      totalPRs: data.totalPRs,
      gatesCount: data.gateStats.length
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load details'
    logger.error('Error fetching leaderboard details:', err)
  } finally {
    loading.value = false
  }
}

const fetchFilterOptions = async () => {
  try {
    const [teamsData, gatesData] = await Promise.all([
      leaderboardService.fetchTeams(),
      leaderboardService.fetchQualityGates()
    ])
    teams.value = teamsData.teams
    subTeams.value = teamsData.subTeams
    qualityGates.value = gatesData.gates
  } catch (err) {
    logger.error('Failed to fetch filter options:', err)
  }
}

const onTeamChange = () => {
  // Reset sub-team when team changes so stale combinations are avoided.
  filters.subTeam = ''
  fetchDetails()
}

const goBack = () => {
  const query: Record<string, string> = {
    timeRange: filters.timeRange,
    groupBy: filters.subTeam ? 'subTeam' : 'team'
  }

  if (filters.team) {
    query.team = filters.team
  }

  if (filters.qualityGate) {
    query.qualityGate = filters.qualityGate
  }

  router.push({
    path: '/quality-gates/compliance',
    query
  })
}

const navigateToPRList = () => {
  const query: Record<string, string> = {}
  if (filters.team) query.team = filters.team
  if (filters.subTeam) query.subTeam = filters.subTeam
  if (filters.qualityGate) query.qualityGate = filters.qualityGate
  if (filters.timeRange) query.timeRange = filters.timeRange

  router.push({
    path: '/quality-gates/compliance/prs',
    query
  })
}

const navigateToPRListForGate = (gateName: string) => {
  const query: Record<string, string> = {}
  if (filters.team) query.team = filters.team
  if (filters.subTeam) query.subTeam = filters.subTeam
  query.qualityGate = gateName
  if (filters.timeRange) query.timeRange = filters.timeRange

  router.push({
    path: '/quality-gates/compliance/prs',
    query
  })
}

const formatGeneratedAt = (isoString: string): string => {
  try {
    const date = new Date(isoString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  } catch {
    return isoString
  }
}

const sortBy = (column: typeof sortState.column) => {
  if (sortState.column === column) {
    // Toggle order if clicking the same column
    sortState.order = sortState.order === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column and default to descending for numbers, ascending for names
    sortState.column = column
    sortState.order = column === 'name' ? 'asc' : 'desc'
  }
}

// Initialize from route query params
const initFromRoute = () => {
  filters.team = (route.query.team as string) || ''
  filters.subTeam = (route.query.subTeam as string) || ''
  filters.qualityGate = (route.query.qualityGate as string) || ''
  filters.timeRange = ((route.query.timeRange as string) || '7d') as TimeRange
}

// Watch for route changes
watch(
  () => route.query,
  () => {
    initFromRoute()
    if (filters.team) {
      fetchDetails()
    }
  },
  { immediate: false }
)

// Lifecycle
onMounted(() => {
  initFromRoute()
  fetchFilterOptions()
  if (filters.team) {
    fetchDetails()
  } else {
    error.value = 'Team parameter is required. Please navigate from the compliance view.'
  }
})
</script>
